import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import type { MediaSetting } from "@circle-react/types/MediaSetting";
import type { VideoMetadata } from "../../VideoPlayer/types";
import type { MediaSettingFormData } from "../types";
import { ChaptersList } from "./ChaptersList";
import { MediaPreview } from "./MediaPreview";
import { MediaSettings } from "./MediaSettings";
import { createChaptersWebVtt } from "./utils/webVttHelpers";

interface EditTabProps {
  customThumbnailUrl: string | null;
  mediaSettings: MediaSetting;
  onCustomThumbnailUrlChange: (url: string | null) => void;
}

export const EditTab = ({
  customThumbnailUrl,
  mediaSettings,
  onCustomThumbnailUrlChange,
}: EditTabProps) => {
  const { watch } = useFormContext<MediaSettingFormData>();
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const shouldMakeDownloadable = watch("make_downloadable");
  const chapters = watch("chapters");

  const mediaSettingsPreview: MediaSetting = {
    ...mediaSettings,
    custom_thumbnail_url: customThumbnailUrl,
    make_downloadable: shouldMakeDownloadable,
  };

  const chaptersUrl = useMemo(() => {
    if (!chapters || chapters.length === 0) return;

    const vttContent = createChaptersWebVtt(chapters, duration);
    const blob = new Blob([vttContent], { type: "text/vtt" });

    return URL.createObjectURL(blob);
  }, [chapters, duration]);

  const setDurationFromMetadata = (metadata: VideoMetadata) =>
    setDuration(metadata.duration);

  return (
    <div className="flex h-full">
      <div className="flex w-full flex-col overflow-auto md:w-96">
        <MediaSettings
          mediaSettings={mediaSettings}
          onCustomThumbnailUrlChange={onCustomThumbnailUrlChange}
        />
      </div>
      <div className="border-primary hidden flex-1 overflow-auto border-l md:flex md:justify-center">
        <div className="flex w-full max-w-2xl flex-col gap-8 2xl:max-w-4xl">
          <div className="flex flex-col gap-8 p-8">
            <MediaPreview
              mediaSettings={mediaSettingsPreview}
              chaptersUrl={chaptersUrl}
              onTimeUpdate={setCurrentTime}
              onLoadedMetadata={setDurationFromMetadata}
            />
            <ChaptersList
              currentTime={currentTime}
              mediaSettings={mediaSettingsPreview}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
